import React from "react";
import { Box, FormLabel, Table, TableHead, TableBody, TableRow, TableCell, TextField, SelectField, Option, FormControlLabel, Radio, RadioGroup, Checkbox } from "@deere/fuel-react";
import { QUESTION_TYPES, TRANSLATIONS, SAMPLE_COUNTRIES } from "../../../../utils/surveyLiterals";
import { getTextTranslation } from "../../../../utils/utils";
import { buildMatrixPreview } from "../../../../utils/matrixQuestionUtils";

const LANG_PROP = TRANSLATIONS.toLocaleLowerCase();
const sortByShowOrder = (items) => [...(items || [])].sort((a, b) => (a.showOrder ?? 0) - (b.showOrder ?? 0));
const smallLabel = { "& .MuiFormControlLabel-label": { fontSize: "12px!important" } };

const MatrixCell = ({ que, currentLang }) => {
  const selectionText = (sel) => getTextTranslation(sel, LANG_PROP, currentLang, false);
  switch (que.type) {
    case QUESTION_TYPES.SELECT:
      return (
        <SelectField value="" displayEmpty>
          <Option disabled value="">Select...</Option>
          {sortByShowOrder(que.selections).map((sel) => <Option key={sel.code} value={sel.code}>{selectionText(sel)}</Option>)}
        </SelectField>
      );
    case QUESTION_TYPES.SEL_CNTRY:
    case QUESTION_TYPES.SEL_CTY_UW:
    case QUESTION_TYPES.SEL_STATE:
      return (
        <SelectField value="" displayEmpty>
          <Option disabled value="">Select...</Option>
          {que.type !== QUESTION_TYPES.SEL_STATE && SAMPLE_COUNTRIES.map((country) => <Option key={country} value={country}>{country}</Option>)}
        </SelectField>
      );
    case QUESTION_TYPES.RADIO:
      return (
        <RadioGroup row name={que.code} sx={{ justifyContent: "center" }}>
          {sortByShowOrder(que.selections).map((sel) => (
            <FormControlLabel key={sel.code} value={sel.code} control={<Radio size="small" />} label={selectionText(sel)} sx={smallLabel} />
          ))}
        </RadioGroup>
      );
    case QUESTION_TYPES.CHECK:
      return (
        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center" }}>
          {sortByShowOrder(que.selections).map((sel) => (
            <FormControlLabel key={sel.code} control={<Checkbox size="small" />} label={selectionText(sel)} sx={smallLabel} />
          ))}
        </Box>
      );
    case QUESTION_TYPES.DATEPICKER:
      return <TextField type="date" />;
    case QUESTION_TYPES.FILE:
      return <TextField type="file" sx={{ "& .MuiInputBase-input": { height: "auto" } }} />;
    case QUESTION_TYPES.OPEN:
      return <TextField multiline minRows={2} />;
    default:
      return <TextField />;
  }
};

const MatrixPreview = ({ matrix, questions, currentLang }) => {
  const { rows, columns } = buildMatrixPreview(matrix, questions);
  const description = matrix.translations?.find((t) => t.locale === currentLang)?.helpText || matrix.helpText;
  const headerCell = { borderRight: "1px solid #e0e0e0" };
  const labelText = { fontSize: "12px", display: "block" };

  return (
    <Box sx={{ width: "100%" }}>
      <FormLabel sx={labelText}>{getTextTranslation(matrix, LANG_PROP, currentLang, false)}</FormLabel>
      {description && <FormLabel sx={{ ...labelText, fontStyle: "italic", mt: 1, whiteSpace: "pre-line" }}>{description}</FormLabel>}
      <Box sx={{ border: "1px solid #e0e0e0", borderRadius: "6px", overflowX: "auto", mt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={headerCell} />
              {columns.map(({ question }) => (
                <TableCell key={question.code} align="center" sx={headerCell}>
                  <FormLabel sx={labelText}>{getTextTranslation(question, LANG_PROP, currentLang, false)}</FormLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIndex) => (
              <TableRow key={row.code} sx={{ backgroundColor: rowIndex % 2 ? "#fafafa" : "inherit" }}>
                <TableCell sx={{ ...headerCell, width: "30%" }}>
                  <FormLabel sx={labelText}>{getTextTranslation(row, LANG_PROP, currentLang, false)}</FormLabel>
                </TableCell>
                {columns.map(({ question, cells }) => (
                  <TableCell key={question.code} align="center" sx={{ borderRight: "1px solid #e0e0e0" }}>
                    <MatrixCell que={cells[rowIndex]} currentLang={currentLang} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default MatrixPreview;
